import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Plus,
  Search,
  User,
  Phone,
  Folder,
  HelpCircle,
  Eye,
  Users as HrCandidatesLogoIcon
} from 'lucide-react';
import CandidateCvDialog from '../components/Hr/CandidateCvDialog';
import DocumentPreviewDrawer from '../components/DocumentPreviewDrawer';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const genderOptions = [
  { value: '', label: 'Non specificato' },
  { value: 'M', label: 'Maschio' },
  { value: 'F', label: 'Femmina' },
  { value: 'Altro', label: 'Altro' }
];

export default function HrCandidati() {
  const location = useLocation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('cognome');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [cognome, setCognome] = useState('');
  const [nome, setNome] = useState('');
  const [genere, setGenere] = useState('');
  const [dataNascita, setDataNascita] = useState('');
  const [cittaResidenza, setCittaResidenza] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [titoloStudioId, setTitoloStudioId] = useState('');
  const [fonteId, setFonteId] = useState('');
  const [posizioneId, setPosizioneId] = useState('');
  const [note, setNote] = useState('');

  const [titoliStudio, setTitoliStudio] = useState([]);
  const [fonti, setFonti] = useState([]);
  const [posizioni, setPosizioni] = useState([]);

  const [filterTitoloStudioId, setFilterTitoloStudioId] = useState('');
  const [filterFonteId, setFilterFonteId] = useState('');
  const [filterPosizioneId, setFilterPosizioneId] = useState('');
  const [filterGenere, setFilterGenere] = useState('');

  const [cvDialogOpen, setCvDialogOpen] = useState(false);
  const [cvDialogCandidato, setCvDialogCandidato] = useState(null);
  const [highlightCandidateId, setHighlightCandidateId] = useState('');
  const [cvPreviewOpen, setCvPreviewOpen] = useState(false);
  const [cvPreviewLoading, setCvPreviewLoading] = useState(false);
  const [cvPreviewUrl, setCvPreviewUrl] = useState('');
  const [cvPreviewFileName, setCvPreviewFileName] = useState('');
  const [cvPreviewItem, setCvPreviewItem] = useState(null);
  const [cvPreviewCandidate, setCvPreviewCandidate] = useState(null);
  const [cvOpenInNewTabLoading, setCvOpenInNewTabLoading] = useState(false);

  const isPreviewableCv = (item) => {
    const mime = String(item?.mime_type || '').toLowerCase();
    const name = String(item?.original_name || '').toLowerCase();
    return mime.includes('pdf') || mime.startsWith('image/') || name.endsWith('.pdf');
  };

  const formatBytes = (bytes) => {
    const n = Number(bytes || 0);
    if (!Number.isFinite(n) || n <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i += 1;
    }
    return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const downloadMedia = async (mediaId, filename) => {
    try {
      const response = await api.get(`/media/${mediaId}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'curriculum';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CV:', error);
      toast.error('Errore nel download del curriculum');
    }
  };

  const openQuickCvPreview = async (row) => {
    if (!row?.id || !row?.has_curriculum) return;
    const candidatoId = String(row.id);
    setCvPreviewCandidate(row);

    if (cvPreviewUrl && String(cvPreviewUrl).startsWith('blob:')) {
      window.URL.revokeObjectURL(cvPreviewUrl);
    }
    setCvPreviewUrl('');
    setCvPreviewFileName('');
    setCvPreviewItem(null);

    setCvPreviewOpen(true);
    setCvPreviewLoading(true);

    try {
      const res = await api.get(`/media/entity/HR_CANDIDATO/${candidatoId}`, {
        params: { category_code: 'HR_CV' }
      });
      const list = Array.isArray(res.data) ? res.data : [];
      const latest = list[0] || null;
      if (!latest?.media_id) {
        toast.info('Nessun curriculum disponibile');
        setCvPreviewOpen(false);
        return;
      }

      if (!isPreviewableCv(latest)) {
        await downloadMedia(latest.media_id, latest.original_name);
        setCvPreviewOpen(false);
        return;
      }

      setCvPreviewItem(latest);
      setCvPreviewFileName(latest.original_name || 'curriculum');

      const presigned = await api.get(`/media/${latest.media_id}/preview-url`);
      const url = presigned?.data?.url || '';
      if (url) {
        setCvPreviewUrl(url);
        return;
      }

      const response = await api.get(`/media/${latest.media_id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || latest.mime_type || 'application/octet-stream'
      });
      const blobUrl = window.URL.createObjectURL(blob);
      setCvPreviewUrl(blobUrl);
    } catch (error) {
      console.error('Error opening quick CV preview:', error);
      toast.error('Errore nell\'apertura del curriculum');
      setCvPreviewOpen(false);
    } finally {
      setCvPreviewLoading(false);
    }
  };

  useEffect(() => {
    const incoming = location.state?.highlightCandidateId ? String(location.state.highlightCandidateId) : '';
    if (!incoming) return;
    setHighlightCandidateId(incoming);
    navigate(location.pathname, { replace: true, state: null });
    const t = setTimeout(() => setHighlightCandidateId(''), 2500);
    return () => clearTimeout(t);
  }, [location.pathname, location.state, navigate]);

  const fetchReferences = async () => {
    try {
      const [tsRes, fRes, pRes] = await Promise.all([
        api.get('/hr/titoli-studio', { params: { onlyActive: 1 } }),
        api.get('/hr/fonti', { params: { onlyActive: 1 } }),
        api.get('/hr/posizioni', { params: { onlyActive: 1 } })
      ]);
      setTitoliStudio(tsRes.data || []);
      setFonti(fRes.data || []);
      setPosizioni(pRes.data || []);
    } catch (error) {
      console.error('Error fetching reference lists:', error);
      toast.error('Errore nel caricamento dei dati di supporto');
    }
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterTitoloStudioId) {
        params.titolo_studio_ids = filterTitoloStudioId;
      }
      if (filterFonteId) {
        params.fonte_ids = filterFonteId;
      }
      if (filterPosizioneId) {
        params.posizione_ids = filterPosizioneId;
      }
      if (filterGenere) {
        params.genere = filterGenere;
      }
      const res = await api.get('/hr/candidati', { params });
      setRows(res.data || []);
    } catch (error) {
      console.error('Error fetching candidati:', error);
      toast.error('Errore nel caricamento dei candidati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferences();
    fetchRows();
  }, []);

  useEffect(() => {
    fetchRows();
  }, [filterTitoloStudioId, filterFonteId, filterPosizioneId, filterGenere]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const parts = [
        row.cognome,
        row.nome,
        row.email,
        row.telefono,
        row.citta_residenza,
        row.fonte_nome,
        row.posizione_titolo,
        row.titolo_studio_nome
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return parts.includes(term);
    });
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return order === 'asc' ? valueA - valueB : valueB - valueA;
      }
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredRows, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    setCognome('');
    setNome('');
    setGenere('');
    setDataNascita('');
    setCittaResidenza('');
    setEmail('');
    setTelefono('');
    setTitoloStudioId('');
    setFonteId('');
    setPosizioneId('');
    setNote('');
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setCognome(item.cognome || '');
    setNome(item.nome || '');
    setGenere(item.genere || '');
    setDataNascita(item.data_nascita ? String(item.data_nascita).slice(0, 10) : '');
    setCittaResidenza(item.citta_residenza || '');
    setEmail(item.email || '');
    setTelefono(item.telefono || '');
    setTitoloStudioId(item.titolo_studio_id || '');
    setFonteId(item.fonte_id || '');
    setPosizioneId(item.posizione_id || '');
    setNote(item.note || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!cognome.trim() || !nome.trim()) {
      toast.error('Nome e cognome sono obbligatori');
      return;
    }

    const payload = {
      cognome: cognome.trim(),
      nome: nome.trim(),
      genere: genere || null,
      data_nascita: dataNascita || null,
      citta_residenza: cittaResidenza || null,
      email: email || null,
      telefono: telefono || null,
      titolo_studio_id: titoloStudioId || null,
      fonte_id: fonteId || null,
      posizione_id: posizioneId || null,
      note: note || null
    };

    try {
      setSaving(true);
      if (editingItem) {
        await api.put(`/hr/candidati/${editingItem.id}`, payload);
        toast.success('Candidato aggiornato');
      } else {
        await api.post('/hr/candidati', payload);
        toast.success('Candidato creato');
      }
      setDialogOpen(false);
      await fetchRows();
    } catch (error) {
      console.error('Error saving candidato:', error);
      const message =
        error.response?.data?.message || 'Errore nel salvataggio del candidato';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/hr/candidati/${deleteTarget.id}`);
      toast.success('Candidato eliminato');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchRows();
    } catch (error) {
      console.error('Error deleting candidato:', error);
      const message =
        error.response?.data?.message || 'Errore nell\'eliminazione del candidato';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatCandidateMain = (row) => {
    const fullName = `${row.cognome || ''} ${row.nome || ''}`.trim();
    return fullName || '-';
  };

  const formatCandidateDetails = (row) => {
    const lines = [];
    if (row.telefono) {
      lines.push(row.telefono);
    }
    if (row.email) {
      lines.push(row.email);
    }
    return lines.join(' • ');
  };

  const formatGenderLabel = (value) => {
    const item = genderOptions.find((g) => g.value === value);
    return item ? item.label : '';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HrCandidatesLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Candidati
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione anagrafica candidati e pipeline di selezione
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus size={18} />}
          onClick={openCreateDialog}
        >
          Nuovo candidato
        </Button>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, email, telefono, fonte..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 360 }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="filter-genere-label">Sesso</InputLabel>
              <Select
                labelId="filter-genere-label"
                label="Sesso"
                value={filterGenere}
                onChange={(e) => setFilterGenere(e.target.value)}
              >
                {genderOptions.map((opt) => (
                  <MenuItem key={opt.value || 'any'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="filter-titolo-label">Titolo di studio</InputLabel>
              <Select
                labelId="filter-titolo-label"
                label="Titolo di studio"
                value={filterTitoloStudioId}
                onChange={(e) => setFilterTitoloStudioId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Tutti</em>
                </MenuItem>
                {titoliStudio.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="filter-fonte-label">Fonte</InputLabel>
              <Select
                labelId="filter-fonte-label"
                label="Fonte"
                value={filterFonteId}
                onChange={(e) => setFilterFonteId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Tutte</em>
                </MenuItem>
                {fonti.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="filter-posizione-label">Posizione</InputLabel>
              <Select
                labelId="filter-posizione-label"
                label="Posizione"
                value={filterPosizioneId}
                onChange={(e) => setFilterPosizioneId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Tutte</em>
                </MenuItem>
                {posizioni.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.titolo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'cognome' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'cognome'}
                    direction={orderBy === 'cognome' ? order : 'asc'}
                    onClick={() => handleRequestSort('cognome')}
                  >
                    Candidato
                  </TableSortLabel>
                </TableCell>
                <TableCell>Sesso</TableCell>
                <TableCell sortDirection={orderBy === 'fonte_nome' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'fonte_nome'}
                    direction={orderBy === 'fonte_nome' ? order : 'asc'}
                    onClick={() => handleRequestSort('fonte_nome')}
                  >
                    Fonte
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'posizione_titolo' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'posizione_titolo'}
                    direction={orderBy === 'posizione_titolo' ? order : 'asc'}
                    onClick={() => handleRequestSort('posizione_titolo')}
                  >
                    Posizione
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ width: 160, minWidth: 160, maxWidth: 160, pr: 0.25 }}
                  sortDirection={orderBy === 'titolo_studio_nome' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'titolo_studio_nome'}
                    direction={orderBy === 'titolo_studio_nome' ? order : 'asc'}
                    onClick={() => handleRequestSort('titolo_studio_nome')}
                  >
                    Titolo di studio
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ width: 96, minWidth: 96, whiteSpace: 'nowrap', pl: 0.25, pr: 0.25 }}
                  sortDirection={orderBy === 'total_colloqui' ? order : false}
                >
                  <Tooltip title="Numero colloqui + stato ultimo colloquio: P=Programmato, S=Sostenuto, C=Chiuso">
                    <TableSortLabel
                      active={orderBy === 'total_colloqui'}
                      direction={orderBy === 'total_colloqui' ? order : 'asc'}
                      onClick={() => handleRequestSort('total_colloqui')}
                    >
                      Colloqui
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ width: 120, minWidth: 120, whiteSpace: 'nowrap', pl: 1, pr: 1 }}
                >
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun candidato presente
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      transition: 'background-color 200ms ease, box-shadow 200ms ease',
                      backgroundColor:
                        highlightCandidateId && String(row.id) === String(highlightCandidateId)
                          ? 'rgba(79, 70, 229, 0.10)'
                          : 'transparent',
                      boxShadow:
                        highlightCandidateId && String(row.id) === String(highlightCandidateId)
                          ? 'inset 0 0 0 2px rgba(79, 70, 229, 0.35)'
                          : 'none'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'rgba(37, 99, 235, 0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mt: 0.3
                          }}
                        >
                          <User size={18} color="#2563eb" />
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap' }}>
                            <Typography sx={{ fontWeight: 600 }}>
                              {formatCandidateMain(row)}
                            </Typography>
                            {(() => {
                              const lastStato = row.last_colloquio_stato ? String(row.last_colloquio_stato) : '';
                              const lastEsito = row.last_colloquio_esito ? String(row.last_colloquio_esito) : '';
                              const isClosed =
                                lastStato === 'chiuso' ||
                                lastStato === 'assunto' ||
                                lastStato === 'rifiutato' ||
                                lastStato === 'non_idoneo';
                              if (!isClosed) return null;

                              const label =
                                lastEsito === 'positivo' || lastEsito === 'offerta_fatta'
                                  ? 'Chiuso • Positivo'
                                  : lastEsito === 'negativo'
                                  ? 'Chiuso • Negativo'
                                  : lastEsito === 'offerta_rifiutata'
                                  ? 'Chiuso • Offerta rifiutata'
                                  : 'Chiuso';

                              const chipColor =
                                lastEsito === 'positivo' || lastEsito === 'offerta_fatta'
                                  ? 'success'
                                  : lastEsito === 'negativo' || lastEsito === 'offerta_rifiutata'
                                  ? 'error'
                                  : 'default';

                              return (
                                <Chip
                                  label={label}
                                  size="small"
                                  color={chipColor}
                                  variant="outlined"
                                  sx={{ borderRadius: '10px' }}
                                />
                              );
                            })()}
                          </Box>
                          {row.telefono && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.25
                              }}
                            >
                              <Phone size={14} color="#64748b" />
                              <Typography variant="body2" color="text.secondary">
                                {row.telefono}
                              </Typography>
                            </Box>
                          )}
                          {row.email && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.25 }}
                            >
                              {row.email}
                            </Typography>
                          )}
                          {!row.telefono && !row.email && (
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{ mt: 0.25 }}
                            >
                              Nessun recapito
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.genere ? (
                        <Chip
                          label={formatGenderLabel(row.genere)}
                          size="small"
                          color="default"
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.fonte_nome ? (
                        <Chip label={row.fonte_nome} size="small" />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.posizione_titolo ? (
                        <Chip label={row.posizione_titolo} size="small" />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 160, minWidth: 160, maxWidth: 160, pr: 0.25 }}>
                      {row.titolo_studio_nome ? (
                        <Chip label={row.titolo_studio_nome} size="small" />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ width: 96, minWidth: 96, pl: 0.25, pr: 0.25 }}
                    >
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {(() => {
                          const lastStato = row.last_colloquio_stato ? String(row.last_colloquio_stato) : '';
                              const lastEsito = row.last_colloquio_esito ? String(row.last_colloquio_esito) : '';
                          const isClosed =
                            lastStato === 'chiuso' ||
                            lastStato === 'assunto' ||
                            lastStato === 'rifiutato' ||
                            lastStato === 'non_idoneo';

                              const normalizedPhase = isClosed ? 'chiuso' : lastStato;
                              const normalizedEsito =
                                normalizedPhase === 'chiuso'
                                  ? lastEsito === 'assunto'
                                    ? 'positivo'
                                    : lastEsito === 'non_idoneo'
                                    ? 'negativo'
                                    : lastEsito === 'rifiutato'
                                    ? 'offerta_rifiutata'
                                    : lastEsito
                                  : '';

                              const lastPhaseLabel =
                                normalizedPhase === 'programmato'
                                  ? 'Programmato'
                                  : normalizedPhase === 'sostenuto'
                                  ? 'Sostenuto'
                                  : normalizedPhase === 'chiuso'
                                  ? 'Chiuso'
                                  : normalizedPhase
                                  ? String(normalizedPhase)
                                  : '';

                              const lastPhaseShort =
                                normalizedPhase === 'programmato'
                                  ? 'P'
                                  : normalizedPhase === 'sostenuto'
                                  ? 'S'
                                  : normalizedPhase === 'chiuso'
                                  ? 'C'
                                  : '';

                              const lastEsitoLabel =
                                normalizedEsito === 'positivo'
                                  ? 'Positivo'
                                  : normalizedEsito === 'negativo'
                                  ? 'Negativo'
                                  : normalizedEsito === 'da_approfondire'
                                  ? 'Da approfondire'
                                  : normalizedEsito === 'secondo_colloquio'
                                  ? 'Secondo colloquio'
                                  : normalizedEsito === 'offerta_fatta'
                                  ? 'Offerta fatta'
                                  : normalizedEsito === 'offerta_rifiutata'
                                  ? 'Offerta rifiutata'
                                  : normalizedEsito
                                  ? String(normalizedEsito)
                                  : '';

                              const phaseColor =
                                normalizedPhase === 'chiuso'
                                  ? normalizedEsito === 'positivo' || normalizedEsito === 'offerta_fatta'
                                    ? 'success'
                                    : normalizedEsito === 'negativo' || normalizedEsito === 'offerta_rifiutata'
                                    ? 'error'
                                    : 'default'
                                  : normalizedPhase === 'programmato'
                                  ? 'info'
                                  : normalizedPhase === 'sostenuto'
                                  ? 'warning'
                                  : 'default';

                              const countChipColor = row.total_colloqui > 0 ? phaseColor : 'default';
                              const countChipLabel =
                                row.total_colloqui > 0 && lastPhaseShort
                                  ? `${row.total_colloqui} ${lastPhaseShort}`
                                  : `${row.total_colloqui || 0}`;

                          const handleOpenColloqui = () => {
                            const candidatoId = row.id ? String(row.id) : '';
                            navigate(
                              `/hr/colloqui?candidatoId=${encodeURIComponent(candidatoId)}`,
                              { state: { candidatoIdFilter: candidatoId } }
                            );
                          };

                          const handleCreateColloquio = () => {
                            if (isClosed) return;
                            const candidatoId = row.id ? String(row.id) : '';
                            navigate(
                              `/hr/colloqui?openNew=1&candidatoId=${encodeURIComponent(candidatoId)}`,
                              { state: { openNew: true, candidatoId, returnToCandidates: true } }
                            );
                          };

                          return (
                            <>
                        {row.total_colloqui > 0 ? (
                          <Tooltip
                            title={
                              lastPhaseLabel
                                ? `Ultimo colloquio: ${lastPhaseLabel}${lastEsitoLabel ? ` • ${lastEsitoLabel}` : ''}`
                                : 'Apri colloqui'
                            }
                          >
                            <Chip
                              label={countChipLabel}
                              size="small"
                              color={countChipColor}
                              variant="outlined"
                              onClick={handleOpenColloqui}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Apri colloqui">
                            <Chip
                              label="0"
                              size="small"
                              color="default"
                              variant="outlined"
                              onClick={handleOpenColloqui}
                            />
                          </Tooltip>
                        )}
                        <Tooltip title={isClosed ? 'Candidato chiuso: non è possibile creare nuovi colloqui' : 'Crea colloquio'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={isClosed}
                              onClick={handleCreateColloquio}
                              sx={{
                                width: 28,
                                height: 28,
                                border: '1px solid rgba(148, 163, 184, 0.35)',
                                borderRadius: '10px'
                              }}
                            >
                              <Plus size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ width: 120, minWidth: 120, whiteSpace: 'nowrap', pl: 1, pr: 1 }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.5,
                          flexWrap: 'nowrap',
                          minWidth: 'max-content'
                        }}
                      >
                        <Tooltip
                          title={
                            row.has_curriculum
                              ? 'Visualizza curriculum caricati'
                              : 'Nessun curriculum: clicca per caricare'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              setCvDialogCandidato(row);
                              setCvDialogOpen(true);
                            }}
                            sx={{
                              width: 32,
                              height: 32,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {row.has_curriculum ? (
                              <Folder size={17} color="#16a34a" />
                            ) : (
                              <HelpCircle size={17} color="#ef4444" />
                            )}
                          </IconButton>
                        </Tooltip>
                        {row.has_curriculum ? (
                          <Tooltip title="Anteprima ultimo CV">
                            <IconButton
                              size="small"
                              onClick={() => openQuickCvPreview(row)}
                              sx={{
                                width: 32,
                                height: 32,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Eye size={17} color="#0ea5e9" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(row)}
                          sx={{
                            width: 32,
                            height: 32,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#14233c'
                          }}
                        >
                          <EditIcon size={17} color="#14233c" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(row)}
                          sx={{
                            width: 32,
                            height: 32,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ef4444'
                          }}
                        >
                          <DeleteIcon size={17} color="#ef4444" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <CandidateCvDialog
        open={cvDialogOpen}
        onClose={() => {
          setCvDialogOpen(false);
          setCvDialogCandidato(null);
        }}
        candidato={cvDialogCandidato}
        onUpdated={({ candidatoId, hasCurriculum }) => {
          if (!candidatoId) return;
          setRows((prev) =>
            (prev || []).map((r) =>
              String(r.id) === String(candidatoId)
                ? { ...r, has_curriculum: hasCurriculum ? 1 : 0 }
                : r
            )
          );
        }}
      />

      <DocumentPreviewDrawer
        open={cvPreviewOpen}
        onClose={() => {
          if (cvPreviewUrl && String(cvPreviewUrl).startsWith('blob:')) {
            window.URL.revokeObjectURL(cvPreviewUrl);
          }
          setCvPreviewOpen(false);
          setCvPreviewUrl('');
          setCvPreviewFileName('');
          setCvPreviewItem(null);
          setCvPreviewCandidate(null);
        }}
        fileName={cvPreviewFileName}
        fileUrl={cvPreviewUrl}
        loading={cvPreviewLoading}
        details={[
          { label: 'Nome', value: cvPreviewItem?.original_name || '-' },
          { label: 'Tipo', value: 'Curriculum candidato' },
          {
            label: 'Formato',
            value:
              String(cvPreviewItem?.mime_type || '').toLowerCase().includes('pdf') ||
              String(cvPreviewItem?.original_name || '').toLowerCase().endsWith('.pdf')
                ? 'PDF'
                : String(cvPreviewItem?.mime_type || '').toLowerCase().startsWith('image/')
                ? 'Immagine'
                : cvPreviewItem?.mime_type || '-'
          },
          { label: 'Dimensione', value: formatBytes(cvPreviewItem?.size_bytes) },
          {
            label: 'Caricato il',
            value: cvPreviewItem?.created_at ? new Date(cvPreviewItem.created_at).toLocaleString('it-IT') : '-'
          },
          {
            label: 'Candidato',
            value: cvPreviewCandidate ? `${cvPreviewCandidate.cognome || ''} ${cvPreviewCandidate.nome || ''}`.trim() : '-'
          },
          {
            label: 'Fonte',
            value: cvPreviewCandidate?.fonte_nome || '-'
          },
          {
            label: 'Posizione',
            value: cvPreviewCandidate?.posizione_titolo || '-'
          }
        ]}
        onOpenInNewTab={async () => {
          if (!cvPreviewItem?.media_id) return;
          try {
            setCvOpenInNewTabLoading(true);
            const presigned = await api.get(`/media/${cvPreviewItem.media_id}/preview-url`);
            const url = presigned?.data?.url || cvPreviewUrl || '';
            if (url) {
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          } catch (_e) {
            if (cvPreviewUrl) {
              window.open(cvPreviewUrl, '_blank', 'noopener,noreferrer');
            }
          } finally {
            setCvOpenInNewTabLoading(false);
          }
        }}
        openInNewTabLoading={cvOpenInNewTabLoading}
        onDownload={
          cvPreviewItem?.media_id
            ? () => downloadMedia(cvPreviewItem.media_id, cvPreviewItem.original_name)
            : null
        }
        downloadDisabled={!cvPreviewItem?.media_id}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Stai per eliminare definitivamente il candidato:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {deleteTarget ? `${deleteTarget.cognome || ''} ${deleteTarget.nome || ''}`.trim() : '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Questa operazione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
            disabled={deleteLoading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => (saving ? null : setDialogOpen(false))}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingItem ? 'Modifica candidato' : 'Nuovo candidato'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cognome"
                value={cognome}
                onChange={(e) => setCognome(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="genere-label">Sesso</InputLabel>
                <Select
                  labelId="genere-label"
                  label="Sesso"
                  value={genere}
                  onChange={(e) => setGenere(e.target.value)}
                >
                  {genderOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Data di nascita"
                type="date"
                value={dataNascita}
                onChange={(e) => setDataNascita(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Città di residenza"
                value={cittaResidenza}
                onChange={(e) => setCittaResidenza(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                type="email"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Telefono"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="titolo-studio-label">Titolo di studio</InputLabel>
                <Select
                  labelId="titolo-studio-label"
                  label="Titolo di studio"
                  value={titoloStudioId}
                  onChange={(e) => setTitoloStudioId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Nessuno</em>
                  </MenuItem>
                  {titoliStudio.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="fonte-label">Fonte</InputLabel>
                <Select
                  labelId="fonte-label"
                  label="Fonte"
                  value={fonteId}
                  onChange={(e) => setFonteId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Nessuna</em>
                  </MenuItem>
                  {fonti.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="posizione-label">Posizione</InputLabel>
                <Select
                  labelId="posizione-label"
                  label="Posizione"
                  value={posizioneId}
                  onChange={(e) => setPosizioneId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Nessuna</em>
                  </MenuItem>
                  {posizioni.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.titolo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
